import { Injectable, OnDestroy } from '@angular/core';
import { Route, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';

import { CerAppRouteService, RouteCount } from 'src/cer-app/cer-app-route/cer-app-route.service';

import { UserStatusVm, VoucherApprovalStatusAllEnum, VoucherApprovalStatusEnum, VoucherMessageChannelTypeEnum, VoucherMessageStatusAllEnum, VoucherMessageStatusEnum } from '../api';
import { CerSessionService } from 'src/cer/cer-session/cer-session.service';
import { CerSessionUserService } from 'src/cer/cer-session/cer-session-user.service';
import { CerNavigationNode } from '@app/app.module-generated';

@Injectable()
export class VoucherSidenavStateService implements OnDestroy {
  private subscriptionManager$: Subscription = new Subscription();
  private userId: number;
  private userStatusVm: UserStatusVm;

  constructor(private router: Router,
    private session: CerSessionService,
    private routeService: CerAppRouteService,
    private metaUserService: CerSessionUserService
  ) {
    this.subscriptionManager$.add(
      combineLatest([this.session.user.user$, this.session.userStatusVm$, this.session.userNavigation.navigation$]).subscribe(
        ([user, statusVm, navigations]) => {
          this.userId = user?.id;
          this.userStatusVm = statusVm;
          if (navigations) {
            this.userCountRead(navigations);
          }
        })
    );
  }

  ngOnDestroy() {
    this.subscriptionManager$.unsubscribe();
  }

  private userCountRead(navigations: CerNavigationNode[]) {
    if (this.userId && this.userStatusVm) {
      var routeCounts: RouteCount[] = [];

      this.router.config.forEach(r => { this.countByRoute(routeCounts, r) });
      routeCounts = routeCounts.filter(r => r.nodeId.substring(0, 5) != 'sales');

      // Vendor Voucher Sum fields
      const financeNode = navigations.find(n => n.nodeId == 'finance');
      if (financeNode) {
        var financeCount: number = null;
        if (financeNode.childNodes.find(n => n.nodeId == 'finance/voucher-vendor')) {
          var channelType: VoucherMessageChannelTypeEnum = VoucherMessageChannelTypeEnum.VendorVoucher;
          var count: number = this.voucherApprovalStatusCount(channelType, VoucherApprovalStatusAllEnum.Approved, false) + this.voucherApprovalStatusCount(channelType, VoucherApprovalStatusAllEnum.Created, false);
          var countMsg: number = this.voucherMessageStatusCount(channelType, VoucherMessageStatusAllEnum.Inbox) +
            this.voucherMessageStatusCount(channelType, VoucherMessageStatusAllEnum.OnHold);
          routeCounts.push(addNodeId('voucher-vendor/voucher', count));
          routeCounts.push(addNodeId('voucher-vendor/message', countMsg));
          routeCounts.push(addNodeId('voucher-vendor', count + countMsg));
          financeCount = count + countMsg;
        }

        // Expense voucher Sum fields
        if (financeNode.childNodes.find(n => n.nodeId == 'finance/voucher-expense')) {
          channelType = VoucherMessageChannelTypeEnum.ExpenseVoucher;
          count = this.voucherApprovalStatusCount(channelType, VoucherApprovalStatusAllEnum.Approved, false) + this.voucherApprovalStatusCount(channelType, VoucherApprovalStatusAllEnum.Created, false);
          countMsg = this.voucherMessageStatusCount(channelType, VoucherMessageStatusAllEnum.Inbox) + this.voucherMessageStatusCount(channelType, VoucherMessageStatusAllEnum.OnHold);
          routeCounts.push(addNodeId('voucher-expense/voucher', count));
          routeCounts.push(addNodeId('voucher-expense/message', countMsg));
          routeCounts.push(addNodeId('voucher-expense', count + countMsg));
          financeCount ??= 0;
          financeCount += count + countMsg;
        }
        if (financeCount != null) {
          routeCounts.push(addNodeId('finance', financeCount));
        }
      }

      routeCounts
        .filter(r => !r.nodeId.startsWith('voucher-my') && !r.nodeId.startsWith('finance'))
        .forEach(r => r.nodeId = 'finance/' + r.nodeId);

      // My voucher (any type) Sum fields
      routeCounts.push(addNodeId('voucher-my', this.voucherApprovalStatusCount(null, VoucherApprovalStatusAllEnum.Pending, true)));

      this.routeService.routeCounts$.next(routeCounts);
    }
  }

  private countByRoute(routeCounts: RouteCount[], r: Route) {
    if (r.path && r.data) {
      let data: any = r.data;
      if (data) {
        let count: number = this.voucherApprovalStatusCount(data.channelType, data.approvalStatus, data.myApprovals ? true : false);
        if (count != null) {
          routeCounts.push(addNodeId(r.path, count));
        }
      }
      if (data.messageStatus != null) {
        let count: number = this.voucherMessageStatusCount(data.channelType, data.messageStatus);
        if (count != null) {
          routeCounts.push(addNodeId(r.path, count));
        }
      }
    }
    if (r.children) {
      r.children.forEach(r => { this.countByRoute(routeCounts, r) });
    }
  }

  private voucherApprovalStatusCount(channelType: VoucherMessageChannelTypeEnum, approvalStatusAll: VoucherApprovalStatusAllEnum, my: boolean): number {
    var approvalStatus: VoucherApprovalStatusEnum = approvalStatusAll == VoucherApprovalStatusAllEnum.All ? null : <VoucherApprovalStatusEnum>(<number>approvalStatusAll);
    var count: number = null;
    if (my && approvalStatusAll != null) {
      if (this.userId) {
        this.userStatusVm.voucherStatusCountByUserList.filter(v => v.approver == this.userId && v.status == approvalStatus).forEach(v => count == null ? count = v.count : count += v.count);
      }
    }
    else if (channelType != null) {
      this.userStatusVm.voucherStatusCountByChannelTypeList.filter(v => channelType == v.channelType && (approvalStatusAll == VoucherApprovalStatusAllEnum.All || approvalStatus == v.status)).forEach(v => count == null ? count = v.count : count += v.count);
    }
    return count;
  }

  private voucherMessageStatusCount(channelType: VoucherMessageChannelTypeEnum, messageStatusAll: VoucherMessageStatusAllEnum): number {
    var messageStatus: VoucherMessageStatusEnum = (messageStatusAll == VoucherMessageStatusAllEnum.All ? null : <VoucherMessageStatusEnum>(<number>messageStatusAll));

    var count: number = null;
    this.userStatusVm.messageStatusCountByChannelTypeList
      .filter(m => channelType == m.channelType && (messageStatus == m.status || messageStatusAll == VoucherMessageStatusAllEnum.All))
      .forEach(m => count == null ? count = m.count : count += m.count);
    return count;
  }
}

function addNodeId(nodeId: string, count: number): RouteCount {
  return <RouteCount>{ nodeId: nodeId, count: count };
}

function addUrl(url: string, count: number): RouteCount {
  return <RouteCount>{ nodeId: null, count: count };
}
